/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useParams } from 'react-router-dom';

import BriefHeader from 'features/briefs/BriefHeader/BriefHeader';
import BriefIndexHeader from 'features/briefs/BriefIndexHeader/BriefIndexHeader';
import BriefCardsPagination from 'features/briefs/components/BriefCardsPagination/BriefCardsPagination';
import CreatorBriefIndex from 'features/briefs/components/organisms/CreatorBriefIndex';
import CreatorBriefTabs from 'features/briefs/components/organisms/CreatorBriefTabs';
import { useBriefs } from 'features/briefs/hooks/useBriefs';
import { BriefSearchQueryParams } from 'features/briefs/interfaces';
import { getRequestQuery } from 'features/briefs/searchUtility';
import SplitPanelScroll from 'shared/components/layout/SplitPanelScroll/SplitPanelScroll';

import { useCurrentUser } from 'shared/hooks/useCurrentUser';
import { useFileNotification } from 'shared/hooks/useFileNotification';
import { useQuery } from 'shared/hooks/useQuery';
import { useSearchParams } from 'shared/hooks/useSearchParams';
import { useToasts } from 'shared/hooks/useToasts';
import { breakpoints } from 'shared/styles/styleFunctions';
import { Brief } from 'shared/typings/briefs';
import { BriefStatus, BriefTypes } from 'shared/typings/briefs/enums';
import { UserBriefStatus } from 'shared/typings/userBriefs/enums';
import ProfileInfoPrompt from 'features/briefs/components/molecules/ProfileInfoPrompt';
import { VettingStatus } from 'shared/typings/user/enums';

type BriefsHomeParams = {
  accountId: string;
  organizationId: string;
};

const BriefsBoard: React.FC = () => {
  const params = useParams<BriefsHomeParams>();
  const accountId = +params.accountId;
  const organizationId = +params.organizationId;

  const { getQuery, setQuery } = useQuery();
  const query = getQuery();
  useFileNotification();

  const { briefs, loading, totalBriefs, fetchBriefs, shouldRefreshBriefs } = useBriefs();
  const { isVetted, currentUser } = useCurrentUser();
  const { setWarningToast } = useToasts();

  const isMobile = useMediaQuery({
    maxWidth: breakpoints.md - 1,
  });
  /** ********************************************************* */
  /* the selected brief information */
  /** ********************************************************* */
  // default to the first brief
  const briefId: number | null = useMemo(
    () => (query.item ? +query.item : briefs.length > 0 ? briefs[0].id : null),
    [query, briefs],
  );
  // TODO: Fix Types
  // @ts-ignore
  const currentBrief: Brief = useMemo(() => briefs.find((brief) => brief.id === briefId), [briefId, briefs]);

  /** ********************************************************* */

  const defaultParams = useMemo(() => {
    const getBriefType = () => {
      // show all briefs to users who are registered and pending vetting review
      if (isVetted) {
        return BriefTypes.BRAND;
      }
      if (currentUser.vettingStatus === VettingStatus.REJECTED) {
        return BriefTypes.SYSTEM;
      }
      return undefined;
    };
    const briefParams: BriefSearchQueryParams = {
      page: 1,
      size: 10,
      orderAsc: false,
      orderBy: 'relevance',
      name: '',
      status: BriefStatus.OPEN,
      type: getBriefType(),
      briefCreatorStatus: [UserBriefStatus.NOT_APPLIED, UserBriefStatus.INVITED, UserBriefStatus.PENDING],
    };
    return briefParams;
  }, [isVetted, currentUser.vettingStatus]);

  const searchParams = useSearchParams(query, defaultParams);

  useEffect(() => {
    const briefSelected: boolean = !!currentBrief;
    const briefsExist = briefs.length > 0;
    const queryMatchesSelectedBrief = briefSelected && +query.item === currentBrief.id;
    const { error, ...rest } = query;
    if (!queryMatchesSelectedBrief && briefSelected) {
      /**
       * if the brief is not being set by the query,
       * aka the first brief was selected
       * then replace the item query from the url
       * */
      setQuery({ item: currentBrief.id, ...rest }, true, true);
    } else if (!briefSelected && briefsExist) {
      /**
       * If no brief is selected but there are briefs,
       * then it means the query doesn't match an existing brief
       * so we'll set it to the first brief
       */
      const briefId = briefs[0].id;
      setQuery({ ...rest, item: briefId, subBriefId: briefId, creatorsBriefId: briefId }, true, true);
    }
    if (briefs.length && error) setWarningToast({ message: query.error });
  }, [currentBrief, briefs, query]);

  const refreshBriefs = useCallback(
    (queryParams = {}) => {
      const params = { ...searchParams, ...queryParams };
      const formattedQuery = getRequestQuery(params, accountId, organizationId);
      fetchBriefs(formattedQuery);
    },
    [searchParams, accountId, organizationId],
  );

  useEffect(() => {
    if (searchParams.type === BriefTypes.SYSTEM && isVetted) {
      setQuery({ ...query, type: BriefTypes.BRAND }, true, true);
    }
  }, [isVetted, searchParams.type, query]);

  useEffect(() => {
    refreshBriefs();
  }, [refreshBriefs]);

  useEffect(() => {
    if (shouldRefreshBriefs) {
      if (totalBriefs && briefs.length === 0) {
        setQuery({ ...query, page: query.page - 1 }, true, true);
      } else if (totalBriefs && briefs.length !== searchParams.size) {
        refreshBriefs();
      } else {
        refreshBriefs({ type: isVetted ? BriefTypes.BRAND : BriefTypes.SYSTEM });
      }
    }
  }, [shouldRefreshBriefs, refreshBriefs, isVetted]);

  return (
    <>
      <ProfileInfoPrompt currentUser={currentUser} />
      {isMobile ? (
        <>
          <BriefIndexHeader
            organizationId={organizationId}
            accountId={accountId}
            searchParams={searchParams}
            isMobile
          />
          <BriefCardsPagination
            briefs={briefs}
            totalBriefs={totalBriefs}
            searchParams={searchParams}
            loading={loading}
            organizationId={organizationId}
            accountId={accountId}
          />
        </>
      ) : (
        <SplitPanelScroll
          leftHeaderComponent={
            <BriefIndexHeader organizationId={organizationId} accountId={accountId} searchParams={searchParams} />
          }
          leftComponent={
            <CreatorBriefIndex
              loading={loading}
              selectedItem={briefId}
              searchParams={searchParams}
              briefs={briefs}
              totalBriefs={totalBriefs}
            />
          }
          rightHeaderComponent={<BriefHeader currentBrief={currentBrief} titleIsClickable={Boolean(currentBrief)} />}
          rightComponent={<CreatorBriefTabs brief={currentBrief} loading={loading} />}
        />
      )}
    </>
  );
};

export default BriefsBoard;
